import React, { Suspense, lazy } from "react";
import BonusDiamonds from "./BonusDiamonds";
import BonusYellowDiamonds from "./BonusYellowDiamonds";
import Bonus5TimeTables from "./Bonus5TimeTables";
import BonusPlaneMode from "./BonusPlaneMode";
import MatchThreeGame from "./MatchThreeGame";
import MemoryMatchGame from "./MemoryMatchGame";
import CountPop from "./CountPop";
import TypingQuiz from "./TypingQuiz";
import RhythmTapGame from "./RhythmTapGame";
import PuzzleShuffle from "./PuzzleShuffle";
import UniversalMathBonus from "./UniversalMathBonus";

import BonusGreaterThan from "./BonusGreaterThan";
import BonusDollarsCents from "./BonusDollarsCents";

// Multiplication games
import Bonus12TimeTables from "./Bonus12TimeTables";
import Bonus11TimeTables from "./Bonus11TimeTables";
import Bonus10TimeTables from "./Bonus10TimeTables";
import Bonus9TimeTables from "./Bonus9TimeTables";
import Bonus8TimeTables from "./Bonus8TimeTables";
import Bonus7TimeTables from "./Bonus7TimeTables";
import Bonus6TimeTables from "./Bonus6TimeTables";
import Bonus4TimeTables from "./Bonus4TimeTables";
import Bonus3TimeTables from "./Bonus3TimeTables";
import Bonus2TimeTables from "./Bonus2TimeTables";

import Bonus3CountBy from "./Bonus3CountBy";
import Bonus5CountBy from "./Bonus5CountBy";
import Bonus10CountBy from "./Bonus10CountBy";

// Fractions games
import BonusFractionstoDecimals from "./BonusFractionstoDecimals";
import BonusAddSubtractFractions from "./BonusAddSubtractFractions";
import BonusCompareFractions from "./BonusCompareFractions";
import BonusMultiplyFractions from "./BonusMultiplyFractions";

// Decimals games
import BonusMultiplyDecimals from "./BonusMultiplyDecimals";

// Shapes games
import BonusTriangles from "./BonusTriangles";

// Social Studies games
import BonusVA5Regions from "./BonusVA5Regions";
import BonusContinentsandOceans from "./BonusContinentsandOceans";

// Treasure Quest (the map + match-3 bonus game) is loaded only when a topic
// actually uses it, so its code and art never slow down any other game.
const TreasureQuest = lazy(() => import("../../games/treasureQuest/TreasureQuest"));

const bonusRoundMap = {
  motion: BonusYellowDiamonds,
  force: BonusDiamonds,
  themagicofmagnets: BonusDiamonds,
  thingsplantsneed: BonusDiamonds,
  animalsneed: BonusDiamonds,
  plantsandanimalsneedtheirspace: BonusDiamonds,
  pollination: BonusDiamonds,
  environmentalchange: MemoryMatchGame,
  habitats: BonusDiamonds,
  matter: BonusPlaneMode,

  greaterthanlessthan: BonusGreaterThan,
  dollarsandcents: BonusDollarsCents,

  countby3s: Bonus3CountBy,
  countby5s: Bonus5CountBy,
  countby10s: Bonus10CountBy,

  "12timestable": Bonus12TimeTables,
  "11timestable": Bonus11TimeTables,
  "10timestable": Bonus10TimeTables,
  "9timestable": Bonus9TimeTables,
  "8timestable": Bonus8TimeTables,
  "7timestable": Bonus7TimeTables,
  "6timestable": Bonus6TimeTables,
  "5timestable": Bonus5TimeTables,
  "4timestable": Bonus4TimeTables,
  "3timestable": Bonus3TimeTables,
  "2timestable": Bonus2TimeTables,

  fractionstodecimals: BonusFractionstoDecimals,
  addandsubtractfractions: BonusAddSubtractFractions,
  comparefractions: BonusCompareFractions,
  multiplyfractions: BonusMultiplyFractions,

  multiplydecimals: BonusMultiplyDecimals,

  triangles: BonusTriangles,

  va5regions: BonusVA5Regions,
  continentsandoceans: BonusContinentsandOceans,

  default: BonusDiamonds,
};

const BonusRouter = ({
  slug,
  bonusRoundType = "diamondTap",
  questions = [],
  placeholderImage,
  onComplete,
  grade, // the student's grade — Treasure Quest uses it for difficulty
}) => {
  const normalizedBonusType = String(bonusRoundType || "diamondTap")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");

    const [mathMode, mathValue] = String(bonusRoundType || "").split(":");

if (["timestable", "countby", "addition", "subtraction"].includes(mathMode.toLowerCase())) {
  return (
    <UniversalMathBonus
      mode={mathMode}
      value={Number(mathValue) || 1}
      questions={questions}
      placeholderImage={placeholderImage}
      onBonusComplete={onComplete}
    />
  );
}

  switch (normalizedBonusType) {
    case "memorymatch":
      return (
        <MemoryMatchGame
          questions={questions}
          placeholderImage={placeholderImage}
          onComplete={onComplete}
        />
      );

    case "match3":
    case "matchthree":
      return (
        <MatchThreeGame
          questions={questions}
          placeholderImage={placeholderImage}
          onComplete={onComplete}
        />
      );

    case "countpop":
      return (
        <CountPop
          questions={questions}
          placeholderImage={placeholderImage}
          onComplete={onComplete}
        />
      );

    case "typingquiz":
      return (
        <TypingQuiz
          questions={questions}
          placeholderImage={placeholderImage}
          onComplete={onComplete}
        />
      );

    case "rhythmtap":
      return (
        <RhythmTapGame
          questions={questions}
          placeholderImage={placeholderImage}
          onComplete={onComplete}
        />
      );

    case "puzzleshuffle":
      return (
        <PuzzleShuffle
          questions={questions}
          placeholderImage={placeholderImage}
          onComplete={onComplete}
        />
      );

    case "treasurequest":
      return (
        <Suspense fallback={<div style={{ padding: 60, textAlign: "center", color: "#fff", fontSize: 22, fontWeight: 800 }}>Loading Treasure Quest…</div>}>
          <TreasureQuest grade={grade} onComplete={onComplete} />
        </Suspense>
      );

    case "none":
      onComplete?.(0);
      return null;

    case "diamondtap":
    default: {
      const normalizedSlug = String(slug || "")
        .toLowerCase()
        .replace(/[^a-z0-9]/g, "");

      const BonusComponent =
        bonusRoundMap[normalizedSlug] || bonusRoundMap.default;

      return (
        <BonusComponent
          onBonusComplete={onComplete}
          questions={questions}
          placeholderImage={placeholderImage}
        />
      );
    }
  }
};

export default BonusRouter;